// client/src/features/auth/use-cooldown.ts — a pause a resend button counts down · D-2674, D-2761.
//
// Every mail the auth screens can ask for again is rationed per recipient by the server
// (D-2652): the MFA code once per thirty seconds, the address confirmation link once a minute.
// Inside that window the server refuses — or, for the confirmation link, answers 202 and sends
// nothing — so the button that asks for another one is disabled for the same span and counts it
// down on its own label. The number is always the server's, mirrored where the hook is called
// and held to the C# by the caller's test.
//
// Lifted out of `MfaChallenge.tsx`, which wrote it first (D-2674), when the confirmation link
// became the second mail with a resend button — on three screens at once (D-2761).

import { useCallback, useEffect, useState } from 'react';

interface Clock {
  now: number;
  until: number | null;
}

/**
 * Whole seconds left of a pause, and the function that starts one (D-2674). `now` is set in the
 * same write as the deadline, so the first render of a pause reads its full length rather than a
 * figure computed against the moment the screen mounted; the interval stops itself at zero.
 *
 * `runningFromMount` starts the pause with the screen itself: the signup's panel is reached by
 * a send the server has only just made, so its cooldown is already running (D-2761). One clock
 * reading serves both halves, or the first render could read a second more than the pause.
 */
export function useCooldown(seconds: number, runningFromMount = false): [number, () => void] {
  const [clock, setClock] = useState<Clock>(() => {
    const current = Date.now();
    return { now: current, until: runningFromMount ? current + seconds * 1000 : null };
  });

  const start = useCallback(() => {
    const current = Date.now();
    setClock({ now: current, until: current + seconds * 1000 });
  }, [seconds]);

  const { until } = clock;

  useEffect(() => {
    if (until === null) return undefined;
    const timer = window.setInterval(() => {
      const current = Date.now();
      setClock((previous) => ({ ...previous, now: current }));
      if (current >= until) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [until]);

  return [until === null ? 0 : Math.max(0, Math.ceil((until - clock.now) / 1000)), start];
}
